import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  authService,
  offerService,
  partnerService,
  parsePartnerSearchResults,
} from '../lib/api-services';
import { AuthUser, handleApiError } from '../lib/api-client';
import { AddAdditionalAddOfferForm, AddIndividualOfferForm, AddOfferForm } from '@/shared/partnerschema';



// ==================== AUTH HOOKS ====================

export function useAuthMe(options?: UseQueryOptions<AuthUser, Error>) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.me,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    gcTime: 0,
    ...options,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      queryClient.clear();
      window.location.href = "https://bprm-ai-portal.workfinity.com/login";
    },
    onError: (error) => {
      console.error('Logout error:', handleApiError(error));
    },
  });
}

// GET PARTNERS

export function useGetPartner(
  pageNumber: string,
  pageSize?: string,
  partnerName = "",
  partnerType = "",
  partnerSubType = ""
) {
  return useQuery({
    queryKey: [
      "partners",
      pageNumber,
      pageSize,
      partnerName,
      partnerType,
      partnerSubType,
    ],
    queryFn: () =>
      partnerService.getPartners(
        pageNumber,
        pageSize,
        partnerName,
        partnerType,
        partnerSubType
      ),
  });
}

export function useAddPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: partnerService.addPartner,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["partners"],
      });
    },
    onError: (error) => {
      console.error(handleApiError(error));
    },
  });
}

export function useEditPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: partnerService.editPartner,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["partners"],
      });
    },
    onError: (error) => {
      console.error(handleApiError(error));
    },
  });
}

export function useDeletePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id }: { Id: number | undefined }) => partnerService.deletePartner(Id),
    onError: (error) => {
      console.error(handleApiError(error));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["partners"],
      });
    },
  });
}

export type PartnerSortOption = "Lex" | "Standard" | "Id_based";

export function useSearchPartners(
  searchTerm: string,
  sort: PartnerSortOption = "Lex",
  enabled: boolean = true
) {
  return useInfiniteQuery({
    queryKey: ["searchPartners", searchTerm, sort],
    queryFn: async ({ pageParam = 1 }) => {
      const data = await partnerService.searchPartners(searchTerm, pageParam as number, 20, sort);
      return { data, page: pageParam as number };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const parsed = parsePartnerSearchResults(lastPage.data);
      if (parsed.items.length < 20) {
        return undefined;
      }
      if (parsed.total !== undefined) {
        const totalFetched = allPages.reduce((acc, p) => acc + parsePartnerSearchResults(p.data).items.length, 0);
        if (totalFetched >= parsed.total) {
          return undefined;
        }
      }
      return (lastPage.page as number) + 1;
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

// OFFERS

export function useGetOffers(
  page: number,
  pageSize: number
) {
  return useQuery({
    queryKey: ["offers", page, pageSize],
    queryFn: () =>
      offerService.getOffers(
        page,
        pageSize
      ),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useAddOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
    }: {
      data: AddOfferForm;
    }) => offerService.addOffer(data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["offers"],
      });
    },
  });
}

export function useEditOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.editOffer,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["offers"],
      });
    },
  });
}


export function useIndividualGetOffers(
  page: number,
  pageSize: number
) {
  return useQuery({
    queryKey: ["individualoffers", page, pageSize],
    queryFn: () =>
      offerService.getIndividualOffers(
        page,
        pageSize
      ),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useIndividualAddOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
    }: {
      data: AddIndividualOfferForm;
    }) => offerService.addIndivisualOffer(data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["individualoffers"],
      });
    },
  });
}

export function useIndividualEditOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.editIndivisualOffer,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["individualoffers"],
      });
    },
  });
}

export function useToggleOfferStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.toggleOfferStatus,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["offers"],
      });
    },
  });
}

export function useGetAdditionalOffers(
  offerId: number,
  pageNumber: number,
  pageSize: number
) {
  return useQuery({
    queryKey: [
      "additionalOffers",
      offerId,
      pageNumber,
      pageSize,
    ],
    queryFn: () =>
      offerService.getAdditionalOffers(
        offerId,
        pageNumber,
        pageSize
      ),
    enabled: !!offerId,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useAddAdditionalOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      data,
    }: {
      offerId: number;
      data: AddAdditionalAddOfferForm;
    }) => offerService.addAdditionalOffer(offerId, data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["additionalOffers"],
      });
    },
  });
}

export function useEditAdditionalOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      additionalOfferId,
      data,
    }: {
      offerId: number;
      additionalOfferId: number;
      data: AddAdditionalAddOfferForm;
    }) =>
      offerService.editAdditionalOffer(
        offerId,
        additionalOfferId,
        data
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["additionalOffers"],
      });
    },
  });
}

export function useGetRules(
  page = 1,
  pageSize: number | undefined,
  offerId?: number | null
) {
  return useQuery({
    queryKey: ["rules", page, pageSize, offerId],
    queryFn: () =>
      offerService.getRules(
        page,
        pageSize,
        offerId
      ),
  });
}

export function useGetRuleCompose(ruleId: string) {
  return useQuery({
    queryKey: ["ruleCompose", ruleId],
    queryFn: () =>
      offerService.getRuleCompose(ruleId),
    enabled: !!ruleId,
  });
}

export function useGetAttributes(parentCode: string) {
  return useQuery({
    queryKey: ["attributes", parentCode],
    queryFn: () =>
      offerService.getAttributes(parentCode),
    enabled: !!parentCode,
  });
}

export function useGetAttributes2(parentCode: string) {
  return useQuery({
    queryKey: ["attributes2", parentCode],
    queryFn: () =>
      offerService.getAttributes(parentCode),
    enabled: !!parentCode,
  });
}

export function useGetDataTypes() {
  return useQuery({
    queryKey: ["datatypes"],
    queryFn: offerService.getDataTypes,
  });
}

export function useCreateRuleValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.createRuleValue,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rules"],
      });
    },
  });
}

export function useUpdateRuleValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objverid,
      data,
    }: {
      objverid: string;
      data: AddOfferForm;
    }) =>
      offerService.updateRuleValue(
        objverid,
        data
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rules"],
      });
    },
  });
}

export function useToggleRuleValueStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objverid,
      offId,
    }: {
      objverid: string;
      offId: string;
    }) =>
      offerService.toggleRuleValueStatus(
        objverid,
        offId
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rules"],
      });
    },
  });
}

export function useCodeManagerValues(name?: number, types?: string, parentType?: string) {
  return useQuery({
    queryKey: ["codes", name, types, parentType],
    queryFn: () =>
      offerService.getCodeFilterApi(name, types, parentType),
  });
}

export function useChatWithAI() {
  return useMutation({
    mutationFn: ({ message, sessionId, microservice = "partner", page = 1 }: { message: string; sessionId: string; microservice?: string; page?: number }) => 
      partnerService.chatWithAI(message, sessionId, microservice, page),
  });
}
